import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, tap } from 'rxjs';
import { SubSink } from 'subsink';

import {
  ProductImportActions,
  ProductImportSelectors,
} from '@app/product-import/store/product-import.slice';
import { ErrorActions } from '@error/store/error.slice';
import { getImportTableValidationsTableColumns } from '@products/functions/table.functions';
import { IColumn } from '@products/interfaces/list-table.interface';
import { ProductImportTableValidations } from '@products/interfaces/product-import-table-validations';

@Component({
  selector: 'app-product-import-table-checker',
  templateUrl: './product-import-table-checker.component.html',
  styleUrls: ['./product-import-table-checker.component.scss'],
})
export class ProductImportTableCheckerComponent implements OnInit, OnDestroy {
  uploadTableForm = new FormGroup({
    file: new FormControl(''),
  });

  readonly maxRequestSizeMiB = 512;
  private readonly maxRequestSize = this.maxRequestSizeMiB * 1024 * 1024;

  readonly maxFileSizeMiB = 3;
  private readonly maxFileSize = this.maxFileSizeMiB * 1024 * 1024;

  readonly acceptedTypes: string[] = [
    '.xls',
    '.xlsx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];

  readonly isTablesSaving$ = this.store.select(
    ProductImportSelectors.selectProductImportTablesSaving
  );
  private readonly isTablesSaveErr$ = this.store
    .select(ProductImportSelectors.selectProductImportTablesSaveError)
    .pipe(
      tap((isErr) => {
        if (isErr) {
          this.lastSavedImages = [];
        }
      })
    );

  lastSavedImages: string[] = [];
  private subs = new SubSink();

  readonly isTablesValidating$ = this.store.select(
    ProductImportSelectors.selectProductImportTableValidating
  );
  displayedColumns: IColumn[] = [];
  productImportTableTableSource =
    new MatTableDataSource<ProductImportTableValidations>();

  validateClicked = false;

  constructor(
    private readonly store: Store,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.displayedColumns = getImportTableValidationsTableColumns();
    this.subs.add(
      this.isTablesSaveErr$.subscribe(),
      this.store
        .select(ProductImportSelectors.selectProductImportTableValidationsList)
        .pipe(filter((validations) => !!validations))
        .subscribe((validations) => {
          this.productImportTableTableSource.data = validations;
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onFileChange(event: any) {
    this.lastSavedImages = [];
    const files: File[] = Array.from(event.target.files);
    const fileSizeSum = files
      .map((file) => file.size)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    const largeFilenameList = files
      .filter((file) => file.size >= this.maxFileSize)
      .map((file) => file.name)
      .join(', ');
    const filesWithNotAcceptedTypes = files
      .filter((file) => !this.acceptedTypes.includes(file.type))
      .map((file) => file.name)
      .join(', ');
    const errorMessages: string[] = [];
    if (largeFilenameList) {
      errorMessages.push(
        this.translateService.instant('SHARED.FILE_UPLOAD.ERROR.FILE_SIZE', {
          size: this.maxFileSize / 1024 ** 2,
          unit: 'MiB',
          files: largeFilenameList,
        })
      );
    }
    if (fileSizeSum >= this.maxRequestSize) {
      errorMessages.push(
        this.translateService.instant('SHARED.FILE_UPLOAD.ERROR.REQUEST_SIZE', {
          size: this.maxRequestSize / 1024 ** 2,
          unit: 'MiB',
        })
      );
    }
    if (largeFilenameList || fileSizeSum >= this.maxRequestSize) {
      errorMessages.unshift(
        this.translateService.instant('SHARED.FILE_UPLOAD.ERROR.FILES_TOO_BIG')
      );
    }
    if (filesWithNotAcceptedTypes) {
      errorMessages.push(
        this.translateService.instant('SHARED.FILE_UPLOAD.ERROR.TYPE', {
          files: largeFilenameList,
        })
      );
    }
    if (errorMessages.length > 0) {
      return this.handleError(errorMessages.join('. '));
    }
    this.submitForm(event.target.files);
    this.lastSavedImages = files.map((file) => file.name);
    this.validateClicked = false;
  }

  private handleError(messageOrNull?: string) {
    const message: string =
      messageOrNull ??
      this.translateService.instant('SHARED.FILE_UPLOAD.ERROR.GENERIC');

    this.store.dispatch(
      ErrorActions.handleError({
        errorCode: '',
        globalErrors: {},
        fieldErrors: {},
        message: message,
        traceId: 0,
      })
    );
  }

  private submitForm(files?: FileList) {
    if (files) {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('file', files[i]);
      }
      this.store.dispatch(
        ProductImportActions.saveProductImportTables.trigger({ formData })
      );
    }
  }

  validateTables() {
    this.store.dispatch(
      ProductImportActions.validateProductImportTables.trigger({
        fileName: this.lastSavedImages[0],
      })
    );
    this.validateClicked = true;
  }

  getCellValue(row: Record<string, string>, field: string): string {
    switch (field) {
      default:
        return row[field];
    }
  }
}
